import { of } from 'rxjs';
import { Router } from '@angular/router';
import { catchError } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { Constants } from '../../../constants/constants';
import { Component, OnInit, ViewChild } from '@angular/core';
import { EntidadDto } from '../../../dto/expediente/entidad.dto';
import { ListasService } from '../../../services/listas.service';
import { environment } from '../../../../environments/environment';
import { HistorialDto } from '../../../dto/integrador/historial.dto';
import { DocumentoDto } from '../../../dto/integrador/documento.dto';
import { HistorialService } from '../../../services/historial.service';
import { SpinnerService } from '../../../services/util/spinner.service';
import { FileUploadService } from '../../../services/file-upload.service';
import { AlertaModalService } from '../../../services/util/alerta-modal.service';
import { ModalFechaCorteComponent } from '../modal-fecha-corte/modal-fecha-corte.component';
import { MiddlewareService } from '../../../services/middleware.service';
import { NotificationService } from '../../../services/notification.service';
import { SocketService } from '../../../services/socket.service';
import { ProcessService } from '../../../services/process.service';

@Component({
  selector: 'app-gestion-manual',
  templateUrl: './gestion-manual.component.html',
  styleUrl: './gestion-manual.component.scss',
})
export class GestionManualComponent implements OnInit {
  // Define la lista de historiales
  historial: HistorialDto[] = [];
  // Define la lista de documentos
  documents: DocumentoDto[] = [];
  // Define la lista de entidades
  entity_list: EntidadDto[] = [];
  // Define la entidad seleccionada
  selectedEntity: number | null = 0;
  // Define el mensaje sin datos
  mensaje: string = 'No hay datos disponibles.';
  // Define si se está arrastrando un archivo
  isDragging = false;
  // Define bandera de administrador funcional.
  esAdminFun = false;
  // Define bandera de super administrador.
  esSuperAdmin = false;
  // Define variable ID Entidad
  idEntidad!: number;
  // Multilenguaje
  cargueManualText: any = {};
  // Define el archivo a procesar
  fileToProcess: File | null = null;
  // Define el nombre del archivo a procesar
  fileNameToProcess: string | null = null;
  // Define la fecha de corte
  fechaCorte: string | null = null;
  // Define la referencia al componente ModalFechaCorteComponent
  @ViewChild(ModalFechaCorteComponent)
  modalFechaCorte!: ModalFechaCorteComponent;

  // Constructor de la clase
  // Parámetros:
  // listaService: ListasService - Servicio de listas
  // historialService: HistorialService - Servicio de historial
  // alertService: AlertaModalService - Servicio de alertas modales
  // fileUploadService: FileUploadService - Servicio de carga
  constructor(
    private listaService: ListasService,
    private historialService: HistorialService,
    private alertService: AlertaModalService,
    private fileUploadService: FileUploadService,
    private spinnerService: SpinnerService,
    private route: Router,
    private translate: TranslateService,
    private socketService: SocketService,
    private notificationService: NotificationService,
    private processService: ProcessService,
    private middlewareService: MiddlewareService
  ) {}

  // Método que se ejecuta al inicializar el componente
  ngOnInit(): void {
    const infoInicioSesion = JSON.parse(
      sessionStorage.getItem(Constants.USER_TYPE) ?? '{}'
    );
    if (infoInicioSesion.Nombre === 'Superadministrador') {
      this.esSuperAdmin = true;
      this.esAdminFun = false;
      this.listaService.getEntidades().subscribe(entidades => {
        this.entity_list = entidades;
      });
    } else if (infoInicioSesion.Nombre === 'Administrador Funcional') {
      this.esAdminFun = true;
      this.esSuperAdmin = false;
      this.idEntidad = infoInicioSesion.Id_Entidad;
      this.loadHistorial(this.idEntidad);
    } else {
      this.route.navigateByUrl('/');
    }
    this.translate.stream('cargue-manual').forEach(text => {
      for (const key of Object.keys(text)) {
        this.cargueManualText[key] = text[key];
      }
    });
    this.middlewareService.triggerRefreshAlertAction();
  }

  // Método que obtiene la fecha de corte
  obtenerFechaCorte() {
    const primerRegistroConFechaCorte: HistorialDto | null = this.historial.find(hist => hist.Corte !== null) || null;
    if (primerRegistroConFechaCorte && primerRegistroConFechaCorte.Corte) {
      const [year, month, day] = primerRegistroConFechaCorte.Corte.split('T')[0].split('-');
      this.fechaCorte = `${day}-${month}-${year}`;
      const msg = `${this.cargueManualText['mensaje-parte1']} ${this.fechaCorte}${this.cargueManualText['mensaje-parte2']}`;
      this.alertService.advertencia(
        this.cargueManualText['mensaje-titulo'],
        msg
      );
    } else {
      this.historialService
      .getFechaCorte()
      .pipe(
        catchError(error => {
          console.error('Error al cargar la fecha de corte:', error);
          this.alertService.error(this.cargueManualText['error-fecha']);
          return of({ data: [] });
        })
      )
      .subscribe(response => {
        if ('fechaCorte' in response) {
          this.fechaCorte = response.fechaCorte;
          const msg = `${this.cargueManualText['mensaje-parte1']} ${this.fechaCorte}${this.cargueManualText['mensaje-parte2']}`;
          this.alertService.advertencia(
            this.cargueManualText['mensaje-titulo'],
            msg
          );
        }
      });      
    }
  }

  // Método que se ejecuta al seleccionar una entidad
  // Parámetros:
  // event: any - Evento de selección
  onEntityChange(event: any) {
    console.log('Entidad seleccionada:', this.selectedEntity);
    this.idEntidad = this.esSuperAdmin
      ? this.selectedEntity || 0
      : this.idEntidad;
    this.loadHistorial(this.idEntidad);
  }

  // Método que se ejecuta al cargar el historial
  // Parámetros:
  // idEntidad: number - Identificador de la entidad
  loadHistorial(idEntidad: number) {
    this.historialService
      .getHistorial(idEntidad)
      .pipe(
        catchError(error => {
          console.error('Error al cargar el historial:', error);
          this.alertService.error(this.cargueManualText['error-historial']);
          return of({ data: [] });
        })
      )
      .subscribe(response => {
        if ('historial' in response) {
          const histList = response.historial;
          // Itera sobre la lista de historiales
          histList.forEach((hist: HistorialDto) => {
            // Asigna el link al historial
            hist.Link = `${hist.Hash}/${hist.Archivo}`;
          });
          this.historial = histList;
        }
        // Cargo los documentos
        if ('documentos' in response) {
          const docList = response.documentos;
          // Itera sobre la lista de documentos
          docList.forEach((doc: DocumentoDto) => {
            // Asigna el link al documento
            doc.Link = `${doc.Entidad.toLowerCase()}/${doc.Nombre}.csv`;
          });
          this.documents = docList;
        }

        this.obtenerFechaCorte();
      });
  }

  // Método que se ejecuta al cargar un archivo
  // Parámetros:
  // file: any - Archivo a cargar
  // idEntidad: number - Identificador de la entidad
  // fileName: string - Nombre del archivo
  uploadProcess(file: any, idEntidad: number, fileName: string) {
    this.fileUploadService
      .fileToBase64(file)
      .then(base64 => {
        this.fileUploadService
          .uploadFile(base64, idEntidad, fileName)
          .pipe(
            catchError(error => {
              this.spinnerService.hideSpinner();
              if (error.status === 404) {
                this.alertService.error(
                  this.cargueManualText['error-servicio']
                );
              } else if (error.status === 400) {
                if (error.error) {
                  this.alertService.error(error.error);
                } else {
                  this.alertService.error(
                    this.cargueManualText['error-archivo']
                  );
                }
              } else if (error.status > 500) {
                if (error.error) {
                  this.alertService.error(
                    `${this.cargueManualText['error-archivo']} ${error.error}`
                  );
                } else {
                  this.alertService.error(
                    this.cargueManualText['error-archivo']
                  );
                }
              } else {
                this.alertService.error(this.cargueManualText['error-archivo']);
              }
              return of({ data: [] });
            })
          )
          .subscribe(response => {
            this.spinnerService.hideSpinner();
            if ('message' in response) {
              this.alertService.confirmacion(
                response.message + this.cargueManualText['continuar'],
                this.cargueManualText['archivo-cargado'],
                () => {
                  // Procesar el archivo
                  this.fileUploadService
                    .processFile(
                      response.data,
                      idEntidad,
                      fileName,
                      this.fechaCorte!
                    )
                    .pipe(
                      catchError(error => {
                        this.spinnerService.hideSpinner();
                        console.error('Error al procesar el archivo:', error);
                        if (error.status === 404) {
                          this.alertService.error(
                            this.cargueManualText['error-servicio']
                          );
                        } else if (error.status === 400) {
                          if (error.error) {
                            this.alertService.error(error.error.message);
                            this.loadHistorial(idEntidad);
                          } else {
                            this.alertService.error(
                              this.cargueManualText['error-procesar-archivo']
                            );
                          }
                        } else if (error.status > 500) {
                          if (error.error) {
                            this.alertService.error(
                              `${this.cargueManualText['error-procesar-archivo']} ${error.error}`
                            );
                          } else {
                            this.alertService.error(
                              this.cargueManualText['error-procesar-archivo']
                            );
                          }
                        } else {
                          this.alertService.error(
                            this.cargueManualText['error-procesar-archivo']
                          );
                        }
                        return of({ data: [] });
                      })
                    )
                    .subscribe(response => {
                      this.spinnerService.hideSpinner();
                      if ('message' in response) {
                        this.alertService.exito(
                          this.cargueManualText['mensaje-titulo'],
                          response.message
                        );
                        console.log('Archivo procesado:', response);
                        this.loadHistorial(idEntidad);
                        this.middlewareService.triggerRefreshAlertAction();
                        const usuario = JSON.parse(sessionStorage.getItem(Constants.USER_INFORMATION_STORAGE) ?? '{}');

                        if (usuario && usuario.identificacion && usuario.tipoDocumento && usuario.idUsuario && usuario.tipoUsuario) {
                          console.log('ENTRANDO al if - Iniciando proceso de usuario');
                          this.startUserProcess(
                            usuario.identificacion,
                            usuario.tipoDocumento,
                            usuario.idTipoDocumento,
                            usuario.idUsuario,
                            usuario.tipoUsuario?.Id
                          );
                        } else {
                          console.log('NO ENTRA al if - Alguna validación falló');
                        }
                      }
                    });
                },
                () => {
                  console.log('Cancelado');
                }
              );
            }
          });
      })
      .catch(error => {
        console.error('Error converting file to Base64:', error.error);
      });
  }

  // Método que se ejecuta al seleccionar un archivo
  // Parámetros:
  // event: Event - Evento de selección
  // Retorna un Observable de tipo void
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.idEntidad = this.esSuperAdmin
      ? this.selectedEntity || 0
      : this.idEntidad;
    if (this.idEntidad == 0) {
      input.value = '';
      this.alertService.error(this.cargueManualText['seleccionar-entidad']);
      return;
    }
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      const fileName = file.name;
      input.value = '';
      //this.uploadProcess(file, this.idEntidad, fileName);
      if (this.checkIfFileIsZip(file)) {
        // Guardar el archivo y nombre temporalmente
        this.fileToProcess = file;
        this.fileNameToProcess = fileName;

        // Abrir el modal para seleccionar la fecha
        this.modalFechaCorte.abrirCerrarModal();
      } else {
        this.alertService.error(this.cargueManualText['error-zip']);
      }
    }
  }

  // Método que se ejecuta al arrastrar un archivo
  // Parámetros:
  // event: DragEvent - Evento de arrastre
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = true;
  }

  // Método que se ejecuta al salir del arrastre
  // Parámetros:
  // event: DragEvent - Evento de arrastre
  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
  }

  // Método que valida si el archivo es un Zip
  // Parámetros:
  // file: File - archivo
  checkIfFileIsZip(file: File): boolean {
    const zipMimeTypes = [
      'application/zip',
      'application/x-zip-compressed',
      'multipart/x-zip',
    ];
    return zipMimeTypes.includes(file.type);
  }

  // Método para manejar la fecha seleccionada desde el modal
  onFechaSeleccionada(fecha: Date): void {
    console.log('Fecha seleccionada:', fecha);
    // Convertir la fecha seleccionada al formato compatible con la API
    const fechaCorte = new Date(fecha).toISOString().split('Z')[0];
    this.fechaCorte = fechaCorte;
    console.log('Fecha de corte:', this.fechaCorte);
    // Continuar con el proceso de carga del archivo
    if (this.fileToProcess && this.fileNameToProcess) {
      this.uploadProcess(
        this.fileToProcess,
        this.idEntidad,
        this.fileNameToProcess
      );
    }
  }

  // Método que se ejecuta al soltar un archivo
  // Parámetros:
  // event: DragEvent - Evento de arrastre
  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (this.checkIfFileIsZip(file)) {
        const fileName = file.name;
        this.idEntidad = this.esSuperAdmin
          ? this.selectedEntity || 0
          : this.idEntidad;
        if (this.idEntidad == 0) {
          this.alertService.error(this.cargueManualText['seleccionar-entidad']);
          return;
        }
        //this.uploadProcess(file, this.idEntidad, fileName);
        // Guardar el archivo y nombre temporalmente
        this.fileToProcess = file;
        this.fileNameToProcess = fileName;
        // Abrir el modal para seleccionar la fecha
        this.modalFechaCorte.abrirCerrarModal();
      } else {
        this.alertService.error(this.cargueManualText['error-zip']);
      }
    }
  }

  download(logFileHash: string | undefined): void {
    console.log('Descargando archivo:', logFileHash);
    if (logFileHash) {
      this.fileUploadService.downloadLog(logFileHash).subscribe(
        blob => {
          // Convert the Blob to a text file and trigger download
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${logFileHash}_log.txt`;
          a.click();
          window.URL.revokeObjectURL(url);
        },
        error => {
          console.error('Error downloading log file:', error);
        }
      );
    }
  }

  downloadFile(filePath: string | undefined): void {
    console.log('Descargando archivo:', filePath);
    if (filePath) {
      this.fileUploadService.downloadFile(filePath).subscribe(
        blob => {
          // Convert the Blob to a text file and trigger download
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          // Get the file name from the path
          const parts = filePath.split('/');
          const fileName = parts[parts.length - 1];
          a.download = fileName;
          a.click();
          window.URL.revokeObjectURL(url);
        },
        error => {
          console.error('Error downloading file:', error);
        }
      );
    }
  }

  private startUserProcess(
    numeroIdentificacion: string,
    tipoIdentificacion: string,
    idTipoDocumento: number,
    idUsuario: number,
    idTipoUsuario: number
  ): void {
    if (!numeroIdentificacion || !tipoIdentificacion) {
      this.notificationService.addNotification(
        'error',
        'Número de identificación o tipo de identificación no definidos'
      );
      return;
    }

    this.processService
      .sincronizarExpedientes(
        numeroIdentificacion,
        tipoIdentificacion,
        idTipoDocumento,
        idUsuario,
        idTipoUsuario,
        true
      )
      .subscribe({
        next: () => {
          this.notificationService.addNotification(
            'success',
            'Sincronización de expedientes iniciada exitosamente'
          );
        },
        error: () => {
          this.notificationService.addNotification(
            'error',
            'Error al sincronizar expedientes'
          );
        },
      });

    const cambiosExpedientes: { radicado: string; action: string }[] = [];
    let timeout: any = null;

    this.socketService.listenToExpedienteChanges(data => {
      cambiosExpedientes.push(data);
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(() => {
        if (cambiosExpedientes.length > 0) {
          const creados = cambiosExpedientes
            .filter(item => item.action === 'created')
            .map(item => item.radicado)
            .join(', ');
          const actualizados = cambiosExpedientes
            .filter(item => item.action === 'updated')
            .map(item => item.radicado)
            .join(', ');
          if (creados)
            this.notificationService.addNotification(
              'info',
              `Los siguientes expedientes han sido creados: ${creados}`
            );
          if (actualizados)
            this.notificationService.addNotification(
              'info',
              `Los siguientes expedientes han sido actualizados: ${actualizados}`
            );
          cambiosExpedientes.length = Constants.EXPEDIENTES_LENGTH;
          timeout = null;
        }
      }, Constants.EXPEDIENTE_DEBOUNCE_TIME);
    });
  }

}
